import { Dispatch, SetStateAction, UIEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import styles from "./ScrollView.module.css";
import { ResponseType } from "../../data_provider/gateways/Gateway";
import { ListSearchReqBody } from "../../data_provider/gateways/MapGateway";

interface SortingStatus {
    sortBy: string;
    direction: string;
}


interface ScrollViewProps {
    getData: (...args: any) => Promise<ResponseType>; // 도중에 함수 변화가 없는 경우 useCallBack 반드시 사용할 것
    renderItem: (item: any, index: number) => JSX.Element;
    maxLength?: number;
    filter?: any;
    isSorting?: SortingStatus;
    deleteItem?: { key: string; value: any };
    preserveScroll?: boolean;
}

export interface ScrollViewListResult {
    list: Array<any>;
    count: number;
    pages: number;
}

/**
 * @author bakim
 * @date 2024-10-14 09:16:57
 * @remark 지도 화면 내 목록 스크롤 뷰
 * @param {(...args : any) => Promise<Object> } getData (required) 데이터 호출 함수
 * @param {(item: any, index: number) => JSX.Element} renderItem (required) 영역 내 목록 아이템 렌더 함수
 * @param {number} maxLength 영역 내 최대 호출 가능 아이템
 * @param {any} filter 검색 필터(조건)
 * @returns <ScrollView>
 */
const ScrollView = (props: ScrollViewProps) => {
    const BUNDLE_SIZE = 40; // 로드할 데이터 개수
    const PAGES = 2; // 스크롤 안에 들어갈 페이지 개수, 총 목록 개수는 BUNDLE_SIZE * PAGES

    const [maxLength, setMaxLength] = useState<number>(props.maxLength ?? BUNDLE_SIZE * PAGES);
    const [loadOption, setLoadOption] = useState({ page: 1, isNext: true });
    const listRef = useRef<HTMLDivElement>(null);
    const [data, setData] = useState<Object[]>([]);

    const [lastPage, setLastPage] = useState<number>(0);
    const [scrollPos, setScrollPos] = useState<number>(-1);

    const firstRender = useRef(false); // 25.08.22 ksm useEffect 로 인한 초기 getList 호출 최소화하기 위해 생성

    // 로드할 페이지 값 변화 시에 데이터 불러오기
    useEffect(() => {
        if (loadOption.page == 1) {
            // 첫번째 진입 시 무조건 1회 호출
            getNewList();
        } else if (loadOption.page < lastPage + 1) {
            getList();
        }
    }, [loadOption.page]);

    // 검색 옵션 변경 시 로드 옵션 초기화
    useEffect(() => {
        if (!loadOption.isNext || loadOption.page > 1) {
            setLoadOption({ page: 1, isNext: true });
        } else {
            if (!firstRender.current) {
                firstRender.current = true;
                return;
            }
            // CHECK★ 두번째는 filter{}, 세번째 진입시 filter == {XID}
            getNewList();
        }
        // console.log("Triggered by filter:", props.filter); // FIXME
    }, [props.filter, props.getData, props.isSorting]);

    // 목록 내 데이터 변경 시 스크롤 위치 변경
    useLayoutEffect(() => {
        if (!listRef.current || scrollPos < 0 || loadOption.page < 1) return;
        if (props.preserveScroll) return;
        const scrollTarget = listRef.current.children[scrollPos];
        scrollTarget.scrollIntoView({ block: "center" });
    }, [data]);

    useEffect(() => {
        if (!data.length) return;
    }, [props.getData]);


    useEffect(() => {
        if (!props.deleteItem || data.length === 0) return;

        const { key, value } = props.deleteItem;
        const filtered = data.filter((item: any) => item[key] !== value);
        setData(filtered);
    }, [props.deleteItem]);

    
    
    // 데이터 소팅 테스트중 =================================================================================
    // useEffect(() => {
    //     getNewList();
    // }, [props.isSorting]);
    // =================================================================================

    const getList = async () => {
        const options: ListSearchReqBody = {
            page: loadOption.page,
            count: BUNDLE_SIZE,
            filter: props.filter ?? undefined,
            sortBy: props.isSorting?.sortBy ?? undefined,
            direction: props.isSorting?.direction ?? undefined,
        };

        const response = await props.getData(options);
        const result: ScrollViewListResult = response.flag && response.result ? response.result : { list: [], pages: 0 };

        if (!result) return;

        let _data = data ? [...data] : [];
        let start = 0;
        let end = maxLength;

        const list = result.list;
        let _pos = 0;

        if (loadOption.isNext) {
            _data.push(...list);

            if (_data.length > maxLength) {
                start = _data.length - maxLength;
                end = _data.length;
            }
            _pos = PAGES > 1 ? Math.min(_data.length, BUNDLE_SIZE * PAGES) - BUNDLE_SIZE : 0;
        } else {
            _pos = PAGES > 1 ? BUNDLE_SIZE - 1 : BUNDLE_SIZE + 1;
            _data = [...list, ..._data];
        }

        _data = _data.slice(start, end);

        setData(_data);
        setScrollPos(Math.min(_pos, maxLength - 1));

        if (!lastPage) setLastPage(result.pages);
    };

    const getNewList = async () => {
        const options: ListSearchReqBody = {
            page: 1,
            count: BUNDLE_SIZE,
            filter: props.filter ?? undefined,
            sortBy: props.isSorting?.sortBy ?? undefined,
            direction: props.isSorting?.direction ?? undefined,
        };

        const response = await props.getData(options);
        const result: ScrollViewListResult = response.flag && response.result ? response.result : { list: [], pages: 0 };

        result.count && result.count < PAGES * BUNDLE_SIZE && setMaxLength(result.count);

        let _data: Object[] = [];
        let start = 0;
        let end = PAGES * BUNDLE_SIZE;

        _data.push(...result.list);
        if (_data.length > PAGES * BUNDLE_SIZE) {
            start = _data.length - PAGES * BUNDLE_SIZE;
            end = _data.length;
        }

        _data = _data.slice(start, end);

        setData(_data);
        setScrollPos(0);
        setLastPage(result.pages);
    };

    const onScroll = (e: UIEvent<HTMLDivElement>) => {
        const target = e.currentTarget;

        const scrollTop = target.scrollTop;
        const innerHeight = target.offsetHeight;
        const scrollHeight = target.scrollHeight;

        const isBottom = scrollTop + innerHeight >= scrollHeight;

        // 최하단에 닿을때
        if (loadOption.page < lastPage && isBottom) {
            if (loadOption.isNext) {
                setLoadOption({ ...loadOption, page: loadOption.page + 1 });
            } else {
                const nextPage = loadOption.page == 1 ? loadOption.page + 1 : loadOption.page + PAGES;
                setLoadOption({ isNext: true, page: nextPage });
            }
        }

        // 최상단에 닿을때
        if (loadOption.page !== 1 && scrollTop === 0) {
            if (loadOption.isNext) {
                setLoadOption({ isNext: false, page: loadOption.page - PAGES });
            } else {
                setLoadOption({ ...loadOption, page: loadOption.page - 1 });
            }
        }
    };

    return (
        <div className={`${styles.scroll}`} onScroll={onScroll} ref={listRef}>
            {data &&
                (data.length === 0 ? (
                    <div className={`${styles.contents}`}>{"조회된 데이터가 없습니다."}</div>
                ) : (
                    data.map((item: Object, index: number) => {
                        return props.renderItem(item, index);
                    })
                ))}
        </div>
    );
};

export default ScrollView;
